import type { TODO, piniaPlaceHolder } from "../util/types";
import Version from "./Versions";
import { useNuxtApp } from "nuxt/app";
import type { Metadata } from "~/stores/Member Posts/memberPostsTypes";

interface ComplianceFields {
  postId: string | string[];
  filingRule: string;
  filingRoles: string[];
  doesPostRequireFiling: boolean;
}
interface Subscriptions {
  subscribedToPostActivity: boolean;
  subscribedToPostCommentActivity: boolean;
}

interface PostDetailParams {
  actions?: TODO[];
  changeDescription?: string;
  channelId?: number;
  channelName?: string;
  commentCount?: number;
  completedActionsCount?: number;
  eventEndDateTimeMillis?: number;
  eventStartDateTimeMillis?: number;
  expirationDateTimeMillis?: number;
  hasViewedLastModification?: boolean;
  lastModificationType?: string;
  lastModifiedAuthor?: string;
  lastModifiedDateTimeMillis?: number;
  lastPublishAuthor?: string;
  lastPublishDateTimeMillis?: number;
  lastViewedDateTimeMillis?: number;
  markedAsUnread?: boolean;
  metadata?: Metadata;
  mimeType?: string;
  orderCurrentStatusLabel?: string;
  orderPONumber?: string;
  orderRequestedForDateMillis?: number;
  orderSiteName?: string;
  orderSiteNumber?: string;
  orderSubscription?: string;
  postId: string;
  postType?: string;
  sharedWithDescription?: string;
  subscriptions?: Partial<Subscriptions>;
  thumbnailContent?: TODO | null;
  thumbnailImageUrl?: string;
  title?: string;
  version?: string;
  versionLabel?: string;
  versions?: Version[];
  viewCount?: number;
}
class PostDetail {
  actions!: TODO[];
  changeDescription!: string;
  channelId!: number;
  channelName!: string;
  commentCount!: number;
  completedActionsCount!: number;
  eventEndDateTimeMillis!: number;
  eventStartDateTimeMillis!: number;
  expirationDateTimeMillis!: number;
  hasViewedLastModification!: boolean;
  lastModificationType!: string;
  lastModifiedAuthor!: string;
  lastModifiedDateTimeMillis!: number;
  lastPublishAuthor!: string;
  lastPublishDateTimeMillis!: number;
  lastViewedDateTimeMillis!: number;
  markedAsUnread!: boolean;
  metadata!: Metadata;
  mimeType!: string;
  orderCurrentStatusLabel!: string;
  orderPONumber!: string;
  orderRequestedForDateMillis!: number;
  orderSiteName!: string;
  orderSiteNumber!: string;
  orderSubscription!: string;
  postId!: string;
  postType!: string;
  sharedWithDescription!: string;
  subscriptions!: Partial<Subscriptions>;
  thumbnailContent!: TODO | null;
  thumbnailImageUrl!: string;
  title!: string;
  version!: string;
  versionLabel!: string;
  versions!: Version[];
  viewCount!: number;

  constructor(postDetail: PostDetailParams) {
    this.postId = postDetail.postId;
    this.initBasicFields(postDetail);
    this.initDateFields(postDetail);
    this.initOrderFields(postDetail);
    this.initMetaFields(postDetail);
    this.initVersions(postDetail);
  }

  private initBasicFields(postDetail: PostDetailParams): void {
    this.actions = postDetail.actions ?? [];
    this.changeDescription = postDetail.changeDescription ?? "";
    this.channelId = postDetail.channelId ?? 0;
    this.channelName = postDetail.channelName ?? "";
    this.commentCount = postDetail.commentCount ?? 0;
    this.completedActionsCount = postDetail.completedActionsCount ?? 0;
    this.hasViewedLastModification = postDetail.hasViewedLastModification ?? false;
    this.lastModificationType = postDetail.lastModificationType ?? "";
    this.lastModifiedAuthor = postDetail.lastModifiedAuthor ?? "";
    this.lastPublishAuthor = postDetail.lastPublishAuthor ?? "";
    this.markedAsUnread = postDetail.markedAsUnread ?? false;
    this.metadata = postDetail.metadata ?? {};
    this.mimeType = postDetail.mimeType ?? "";
    this.postType = postDetail.postType ?? "";
    this.sharedWithDescription = postDetail.sharedWithDescription ?? "";
    this.subscriptions = postDetail.subscriptions ?? {};
    this.thumbnailContent = postDetail.thumbnailContent ?? null;
    this.thumbnailImageUrl = postDetail.thumbnailImageUrl ?? "";
    this.title = postDetail.title ?? "";
    this.version = postDetail.version ?? "";
    this.versionLabel = postDetail.versionLabel ?? "";
    this.viewCount = postDetail.viewCount ?? 0;
  }

  private initDateFields(postDetail: PostDetailParams): void {
    this.eventEndDateTimeMillis = postDetail.eventEndDateTimeMillis ?? 0;
    this.eventStartDateTimeMillis = postDetail.eventStartDateTimeMillis ?? 0;
    this.expirationDateTimeMillis = postDetail.expirationDateTimeMillis ?? 0;
    this.lastModifiedDateTimeMillis = postDetail.lastModifiedDateTimeMillis ?? 0;
    this.lastPublishDateTimeMillis = postDetail.lastPublishDateTimeMillis ?? 0;
    this.lastViewedDateTimeMillis = postDetail.lastViewedDateTimeMillis ?? 0;
  }

  private initOrderFields(postDetail: PostDetailParams): void {
    this.orderCurrentStatusLabel = postDetail.orderCurrentStatusLabel ?? "";
    this.orderPONumber = postDetail.orderPONumber ?? "";
    this.orderRequestedForDateMillis = postDetail.orderRequestedForDateMillis ?? 0;
    this.orderSiteName = postDetail.orderSiteName ?? "";
    this.orderSiteNumber = postDetail.orderSiteNumber ?? "";
    this.orderSubscription = postDetail.orderSubscription ?? "";
  }

  private initMetaFields(postDetail: PostDetailParams): void {
    this.metadata = postDetail.metadata ?? {};
  }

  private initVersions(postDetail: PostDetailParams): void {
    this.versions = (postDetail.versions ?? []).map(
      (version) => new Version(version)
    );
  }

  async updateCompliance(): Promise<TODO[]> {
    const { postId, filingRule, filingRoles, doesPostRequireFiling } =
      this as unknown as ComplianceFields;
    const pinia = useNuxtApp().$pinia as piniaPlaceHolder;

    const postIds = Array.isArray(postId) ? postId : [postId];
    const promises = postIds.map((post: string) =>
      pinia.$axios.$put(pinia.$API.posts.updatedPosts(post), {
        compliance: {
          filingRule,
          filingRoles,
          doesPostRequireFiling,
        },
      })
    );
    return await Promise.all(promises);
  }
}

export default PostDetail;
